// Renders sections/wrpg-wishlist.liquid. Reads the resolved handle list from
// window.wrpgWishlist (wrpg-wishlist.js) - guests never get this far, they
// see a "log in" prompt instead (wishlist data only ever exists server-side
// for real customers). For each handle, fetches:
//   1. the card HTML via Shopify's Section Rendering API
//      (?section_id=wrpg-wishlist-card), so we get the exact same
//      fully-featured product card markup used on collection pages, and
//   2. the plain product JSON (/products/{handle}.js), used only to drive
//      the client-side sort/filter panel (tags, price, availability, date).
// Fetched card HTML is parsed with DOMParser and only its .card-wrapper
// element is kept - the <link>/<script> tags accompanying it are discarded
// on purpose (those never execute via innerHTML anyway), which is why
// sections/wrpg-wishlist.liquid preloads every stylesheet/script the card
// needs up front instead.
(() => {
  const root = document.getElementById("wrpg-wishlist-page");
  if (!root) return;

  const loadingEl = root.querySelector(".wrpg-wishlist-page__state--loading");
  const loginEl = root.querySelector(".wrpg-wishlist-page__state--login");
  const emptyEl = root.querySelector(".wrpg-wishlist-page__state--empty");
  const errorEl = root.querySelector(".wrpg-wishlist-page__state--error");
  const toolbarEl = root.querySelector(".wrpg-wishlist-page__toolbar");
  const gridEl = root.querySelector(".wrpg-wishlist-page__grid");
  const sortSelect = root.querySelector("#WrpgWishlistSort");
  const availabilityCheckbox = root.querySelector("#WrpgWishlistFilterAvailable");
  const filterTagsEl = root.querySelector("#WrpgWishlistFilterTags");

  // { handle, li, product }[] - the source of truth for what's currently
  // rendered; filter/sort only ever hide/reorder these, never re-fetch.
  let items = [];
  let activeTags = new Set();
  let onlyAvailable = false;

  function showState(state) {
    loadingEl.hidden = state !== "loading";
    loginEl.hidden = state !== "login";
    emptyEl.hidden = state !== "empty";
    errorEl.hidden = state !== "error";
    toolbarEl.hidden = state !== "grid";
    gridEl.hidden = state !== "grid";
  }

  function fetchCardHtml(handle) {
    return fetch(`/products/${handle}?section_id=wrpg-wishlist-card`)
      .then((response) => (response.ok ? response.text() : null))
      .catch(() => null);
  }

  function fetchProductJson(handle) {
    return fetch(`/products/${handle}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  function extractCard(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.querySelector(".card-wrapper");
  }

  function buildFilterTags() {
    const tagCounts = new Map();
    items.forEach(({ product }) => {
      (product.tags || []).forEach((tag) => {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      });
    });

    filterTagsEl.innerHTML = "";
    [...tagCounts.keys()]
      .sort((a, b) => a.localeCompare(b))
      .forEach((tag) => {
        const label = document.createElement("label");
        label.className = "facets__label facet-checkbox";

        const input = document.createElement("input");
        input.type = "checkbox";
        input.value = tag;
        input.addEventListener("change", () => {
          if (input.checked) {
            activeTags.add(tag);
          } else {
            activeTags.delete(tag);
          }
          applyFilterSort();
        });

        const span = document.createElement("span");
        span.className = "facet-checkbox__text";
        span.textContent = `${tag} (${tagCounts.get(tag)})`;

        label.appendChild(input);
        label.appendChild(span);
        filterTagsEl.appendChild(label);
      });
  }

  function sortComparator(sortValue) {
    switch (sortValue) {
      case "title-ascending":
        return (a, b) => a.product.title.localeCompare(b.product.title);
      case "price-ascending":
        return (a, b) => a.product.price - b.product.price;
      case "price-descending":
        return (a, b) => b.product.price - a.product.price;
      case "created-descending":
      default:
        return (a, b) => new Date(b.product.created_at) - new Date(a.product.created_at);
    }
  }

  function applyFilterSort() {
    items.forEach((item) => {
      const tags = item.product.tags || [];
      const matchesTags = activeTags.size === 0 || tags.some((tag) => activeTags.has(tag));
      const matchesAvailability = !onlyAvailable || item.product.available;
      item.li.hidden = !(matchesTags && matchesAvailability);
    });

    [...items]
      .sort(sortComparator(sortSelect.value))
      .forEach((item) => gridEl.appendChild(item.li));
  }

  function mostExpensiveTitle(product) {
    const variants = [...(product.variants || [])];
    if (variants.length === 0) return "";
    return variants.sort((a, b) => Number(b.price) - Number(a.price))[0].title;
  }

  function variantNamesFor(handle, product) {
    const ids = window.wrpgWishlist.getVariantIds ? window.wrpgWishlist.getVariantIds(handle) : [];
    const concrete = ids.filter(Boolean);
    const hasLegacy = ids.some((id) => id == null || id === "");
    const names = (product.variants || [])
      .filter((variant) => concrete.includes(`gid://shopify/ProductVariant/${variant.id}`))
      .map((variant) => variant.title);
    if (names.length === 0 && hasLegacy) {
      const title = mostExpensiveTitle(product);
      return title ? [title] : [];
    }
    return names;
  }

  function paintVariantBadges() {
    items.forEach((item) => {
      const existing = item.li.querySelector(".wrpg-wishlist-page__variants");
      if (existing) existing.remove();
      const names = variantNamesFor(item.handle, item.product);
      if (names.length === 0) return;
      const badge = document.createElement("p");
      badge.className = "wrpg-wishlist-page__variants caption";
      badge.textContent = names.join(" · ");
      badge.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const heart = item.li.querySelector("[data-wrpg-wishlist-toggle]");
        if (heart) heart.click();
      });
      const info = item.li.querySelector(".card__information") || item.li.querySelector(".card-wrapper");
      if (info) info.appendChild(badge);
    });
  }

  function render(handles) {
    if (handles.length === 0) {
      showState("empty");
      return;
    }

    showState("loading");

    Promise.all(
      handles.map((handle) =>
        Promise.all([fetchCardHtml(handle), fetchProductJson(handle)]).then(([html, product]) => ({
          handle,
          html,
          product,
        }))
      )
    ).then((results) => {
      const valid = results.filter((result) => result.html && result.product);
      if (valid.length === 0) {
        showState("empty");
        return;
      }

      gridEl.innerHTML = "";
      items = valid
        .map(({ handle, html, product }) => {
          const card = extractCard(html);
          if (!card) return null;
          const li = document.createElement("li");
          li.className = "grid__item";
          li.appendChild(card);
          gridEl.appendChild(li);
          return { handle, li, product };
        })
        .filter(Boolean);

      if (items.length === 0) {
        showState("empty");
        return;
      }

      // Newly-inserted cards' heart buttons default to "not saved" (see
      // wrpg-wishlist.js) since they were never touched by the click-driven
      // sync - every one of these cards IS a saved item by definition, so
      // force their hearts to reflect that now that they're in the DOM.
      if (window.wrpgWishlist.sync) window.wrpgWishlist.sync();
      if (window.wrpgExclusions && window.wrpgExclusions.sync) window.wrpgExclusions.sync();
      if (window.wrpgOwnership && window.wrpgOwnership.ready) window.wrpgOwnership.ready();

      paintVariantBadges();
      buildFilterTags();
      applyFilterSort();
      showState("grid");
    });
  }

  sortSelect.addEventListener("change", applyFilterSort);
  availabilityCheckbox.addEventListener("change", () => {
    onlyAvailable = availabilityCheckbox.checked;
    applyFilterSort();
  });

  // Hook into the shared wishlist script's broadcast event so we can remove
  // items from the DOM immediately when the heart button is toggled off.
  document.addEventListener("wrpg:wishlist:change", (event) => {
    if (gridEl.hidden) return; // Not showing the grid anyway (guest/empty/error state)

    const currentHandles = new Set(event.detail.handles);
    items = items.filter((item) => {
      if (currentHandles.has(item.handle)) return true;
      item.li.remove();
      return false;
    });

    if (items.length === 0) {
      showState("empty");
      return;
    }
    paintVariantBadges();
  });

  if (!window.wrpgWishlist) {
    showState("error");
    return;
  }

  window.wrpgWishlist
    .ready()
    .then(() => {
      const mode = window.wrpgWishlist.getMode();
      if (mode === "error") {
        // The wishlist service itself is unreachable (network blip, or the
        // backend route isn't deployed) - distinct from "guest" so we don't
        // wrongly tell a logged-in customer to log in.
        showState("error");
        return;
      }
      if (mode !== "server") {
        showState("login");
        return;
      }
      render(window.wrpgWishlist.getHandles());
    })
    .catch(() => showState("error"));
})();
